import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Container, Typography, Box, TextField, InputAdornment, useMediaQuery, Tabs, Tab, CircularProgress, Alert } from '@mui/material';
import { CatalogPagination } from '../components/CatalogPagination';
import { Search, Palette, Person } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { en } from '../translations/en';
import { AdvancedFilter, FilterCriteria } from '../components/AdvancedFilter';
import { SearchResultsGrid } from '../components/SearchResultsGrid';
import { SearchResultsToolbar, SEARCH_SORT_OPTIONS } from '../components/SearchResultsToolbar';
import { SaveSearchDialog } from '../components/SaveSearchDialog';
import { SavedSearchesMenu } from '../components/SavedSearchesMenu';
import { useBookmarks } from '../hooks/useBookmarks';
import { useSearch } from '../hooks/useSearch';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { useSavedSearches } from '../hooks/useSavedSearches';
import { DisplayNameNotice } from '../components/DisplayNameNotice';
import api from '../utils/api';
import { logger } from '../utils/logger';
import { convertDimensionToMetric } from '../utils/dimensionUtils';
import type { SavedSearch, SearchParams } from '../utils/savedSearchesApi';
import type { SearchScope } from '../contexts/SearchContext';

// Global search default: span everything the caller can access — their own
// items across ALL owned catalogs, all public items (any owner), and every
// delegated catalog — regardless of which catalog is "active" in the nav.
// Sending explicit scopes also suppresses the active-catalog narrowing in
// SearchContext (owner_id / catalogAliId), so search is never silently
// scoped to one catalog. See GL search-cross-catalog fix.
const GLOBAL_SEARCH_SCOPES: SearchScope[] = ['own', 'public', 'delegate'];


// User interface for search results (matches API response)
interface User {
  userId: string;
  name: string;
  email?: string;
  userType?: string;
  aliId?: string;
  artworkCount: number;
  profileImageUrl?: string;
  createdAt?: string;
}


export const Home = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isLaptop = useMediaQuery(theme.breakpoints.down('lg'));
  const { toggleBookmark, isBookmarked } = useBookmarks();
  const { searchTerm, setSearchTerm, search, searchResults, isSearching, error: searchError, total, page, setPage, pageSize, sortBy, setSortBy } = useSearch();
  const { isAuthenticated } = useAuth();
  const { canEditCatalog, permissions } = usePermissions();
  const { savedSearches, loading: savedSearchesLoading, error: savedSearchesError, saveSearch, deleteSearch } = useSavedSearches();
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  // Read initial search query from URL params
  const urlQuery = searchParams.get('q') || '';
  // Use searchTerm from context as local input state, synced with URL
  const [localSearchInput, setLocalSearchInput] = useState(urlQuery || searchTerm);
  // Ref to track if the update came from internal source (prevents bidirectional sync loop)
  const isInternalUpdateRef = useRef(false);
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<'objects' | 'users'>('objects');
  const [advancedFilters, setAdvancedFilters] = useState<FilterCriteria>({});
  const [hasSearched, setHasSearched] = useState(false);
  const [initialSearchDone, setInitialSearchDone] = useState(false);
  const pendingSavedSearchRef = useRef(false);

  // User search state
  const [userSearchResults, setUserSearchResults] = useState<User[]>([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [userSearchError, setUserSearchError] = useState<string | null>(null);

  // Search users via API. Defined before performSearch so it can be called
  // concurrently with the object search. Fixes #1018.
  const searchUsers = useCallback(async (query: string) => {
    if (!api) return;

    setIsSearchingUsers(true);
    setUserSearchError(null);

    try {
      const params = new URLSearchParams();
      if (query) {
        params.append('q', query);
      }
      params.append('size', '50');

      const response = await api.get(`/api/search/users?${params.toString()}`);
      const users: User[] = (response.data.items || []).map((item: Record<string, unknown>) => ({
        userId: item.userId,
        name: item.name || 'Unknown User',
        email: item.email,
        userType: item.userType,
        aliId: item.aliId,
        artworkCount: item.artworkCount || 0,
        profileImageUrl: item.profileImageUrl,
        createdAt: item.createdAt,
      }));
      setUserSearchResults(users);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to search users';
      setUserSearchError(errorMessage);
      setUserSearchResults([]);
    } finally {
      setIsSearchingUsers(false);
    }
  }, []);

  const performSearch = useCallback(async () => {
    const hasFilters = Object.entries(advancedFilters).some(
      ([key, value]) =>
        key !== 'dimensionUnit' &&
        value !== undefined &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0)
    );

    if (!localSearchInput.trim() && !hasFilters) {
      setActiveSearchQuery('');
      setHasSearched(false);
      return;
    }

    setActiveSearchQuery(localSearchInput);
    setHasSearched(true);

    try {
      const searchFilters = {
        title: advancedFilters.artworkTitle || undefined,
        keywords: advancedFilters.keywords || undefined,
        provenance: advancedFilters.provenance || undefined,
        heightFrom: convertDimensionToMetric(advancedFilters.heightFrom, advancedFilters.dimensionUnit),
        heightTo: convertDimensionToMetric(advancedFilters.heightTo, advancedFilters.dimensionUnit),
        widthFrom: convertDimensionToMetric(advancedFilters.widthFrom, advancedFilters.dimensionUnit),
        widthTo: convertDimensionToMetric(advancedFilters.widthTo, advancedFilters.dimensionUnit),
        dateFrom: advancedFilters.dateFrom ? (Number.isNaN(parseInt(advancedFilters.dateFrom, 10)) ? undefined : parseInt(advancedFilters.dateFrom, 10)) : undefined,
        dateTo: advancedFilters.dateTo ? (Number.isNaN(parseInt(advancedFilters.dateTo, 10)) ? undefined : parseInt(advancedFilters.dateTo, 10)) : undefined,
        workType: advancedFilters.workType ? [advancedFilters.workType] : undefined,
        materials: advancedFilters.materials,
        techniques: advancedFilters.techniques,
        creators: advancedFilters.creators ? [advancedFilters.creators] : undefined,
        scopes: GLOBAL_SEARCH_SCOPES,
      };

      // Run object search and user search concurrently so the Users tab
      // shows results immediately without requiring a tab switch. Fixes #1018.
      await Promise.all([
        search(localSearchInput.trim(), searchFilters),
        searchUsers(localSearchInput.trim()),
      ]);
    } catch (error) {
      logger.error('Search failed', error);
    }
  }, [localSearchInput, advancedFilters, search, searchUsers]);

  // Re-trigger search when page or sortBy change
  const prevPageRef = useRef(page);
  const prevSortRef = useRef(sortBy);
  useEffect(() => {
    if (!hasSearched) return;
    if (prevPageRef.current === page && prevSortRef.current === sortBy) return;
    prevPageRef.current = page;
    prevSortRef.current = sortBy;
    performSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sortBy]);

  // Trigger search when advanced filters change (user clicks Apply in AdvancedFilter)
  const advancedFiltersRef = useRef(advancedFilters);
  useEffect(() => {
    // Skip the initial render
    if (advancedFiltersRef.current === advancedFilters) return;
    advancedFiltersRef.current = advancedFilters;
    performSearch();
  }, [advancedFilters, performSearch]);

  // Sync local input with context searchTerm when it changes externally (e.g., from NavBar)
  // Skip if the update originated from this component to prevent bidirectional sync loop
  useEffect(() => {
    // Skip sync if this was an internal update
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }
    // Only sync if the context searchTerm is different from local input
    // This handles the case when user searches from NavBar and lands on Home
    if (searchTerm && searchTerm !== localSearchInput) {
      setLocalSearchInput(searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]); // Intentionally only depend on searchTerm, not localSearchInput

  // Reset initialSearchDone when URL query changes (e.g., browser back navigation)
  // so the search re-triggers with the restored URL params
  const prevUrlQueryRef = useRef(urlQuery);
  useEffect(() => {
    if (urlQuery !== prevUrlQueryRef.current) {
      prevUrlQueryRef.current = urlQuery;
      setInitialSearchDone(false);
    }
  }, [urlQuery]);

  // Execute search on mount if URL has query param or searchTerm is set
  useEffect(() => {
    const queryToSearch = urlQuery || searchTerm;
    if (!queryToSearch || initialSearchDone) return;

    // Use a flag to track if component is still mounted
    let isMounted = true;

    const doInitialSearch = async () => {
      if (!isMounted) return;

      setInitialSearchDone(true);
      setActiveSearchQuery(queryToSearch);
      setHasSearched(true);
      setLocalSearchInput(queryToSearch);

      try {
        // Run object search and user search concurrently. Fixes #1018.
        await Promise.all([
          search(queryToSearch.trim(), {}),
          searchUsers(queryToSearch.trim()),
        ]);
      } catch (error) {
        logger.error('Initial search failed', error);
      }
    };

    // Delay the initial search to avoid cascading renders
    const timeoutId = setTimeout(doInitialSearch, 0);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [urlQuery, searchTerm, initialSearchDone, search, searchUsers]);

  // Update URL and context when search query changes (from local input)
  const handleSearchQueryChange = useCallback((newQuery: string) => {
    setLocalSearchInput(newQuery);
    // Mark this as an internal update to prevent sync loop
    isInternalUpdateRef.current = true;
    setSearchTerm(newQuery); // Sync with context
    // Update URL params without navigation
    if (newQuery.trim()) {
      setSearchParams({ q: newQuery }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [setSearchParams, setSearchTerm]);

  // Build current search params for saving
  const buildSearchParams = useCallback((): SearchParams => ({
    q: localSearchInput.trim() || undefined,
    workType: advancedFilters.workType || undefined,
    materials: advancedFilters.materials?.join(',') || undefined,
    techniques: advancedFilters.techniques?.join(',') || undefined,
    creators: advancedFilters.creators || undefined,
    dateFrom: advancedFilters.dateFrom ? parseInt(advancedFilters.dateFrom, 10) || undefined : undefined,
    dateTo: advancedFilters.dateTo ? parseInt(advancedFilters.dateTo, 10) || undefined : undefined,
    heightFrom: advancedFilters.heightFrom ? parseFloat(advancedFilters.heightFrom) || undefined : undefined,
    heightTo: advancedFilters.heightTo ? parseFloat(advancedFilters.heightTo) || undefined : undefined,
    widthFrom: advancedFilters.widthFrom ? parseFloat(advancedFilters.widthFrom) || undefined : undefined,
    widthTo: advancedFilters.widthTo ? parseFloat(advancedFilters.widthTo) || undefined : undefined,
    provenance: advancedFilters.provenance || undefined,
    title: advancedFilters.artworkTitle || undefined,
    sortBy: sortBy !== 'relevance' ? sortBy : undefined,
    dimensionUnit: advancedFilters.dimensionUnit || undefined,
  }), [localSearchInput, advancedFilters, sortBy]);

  const handleSaveSearch = useCallback(async (name: string) => {
    await saveSearch(name, buildSearchParams());
  }, [saveSearch, buildSearchParams]);

  const handleLoadSavedSearch = useCallback((search: SavedSearch) => {
    const params = search.searchParams;

    // Restore search query
    const query = params.q || '';
    setLocalSearchInput(query);
    isInternalUpdateRef.current = true;
    setSearchTerm(query);
    if (query) {
      setSearchParams({ q: query }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }

    // Validate and restore dimension unit
    const validUnits = ['in', 'cm'] as const;
    const unit = validUnits.includes(params.dimensionUnit as 'in' | 'cm')
      ? (params.dimensionUnit as 'in' | 'cm')
      : 'in';

    // Restore advanced filters
    const restoredFilters: FilterCriteria = {
      artworkTitle: params.title || '',
      provenance: params.provenance || '',
      dateFrom: params.dateFrom?.toString() || '',
      dateTo: params.dateTo?.toString() || '',
      heightFrom: params.heightFrom?.toString() || '',
      heightTo: params.heightTo?.toString() || '',
      widthFrom: params.widthFrom?.toString() || '',
      widthTo: params.widthTo?.toString() || '',
      dimensionUnit: unit,
      workType: params.workType || '',
      materials: params.materials?.split(',').filter(Boolean) || [],
      techniques: params.techniques?.split(',').filter(Boolean) || [],
      creators: params.creators || '',
    };
    setAdvancedFilters(restoredFilters);

    // Validate and restore sort
    const validSorts = SEARCH_SORT_OPTIONS.map(o => o.value) as string[];
    if (params.sortBy && validSorts.includes(params.sortBy)) {
      setSortBy(params.sortBy as typeof sortBy);
    }

    // Mark that a saved search load is pending so the effect triggers search
    pendingSavedSearchRef.current = true;
    setHasSearched(true);
    setActiveSearchQuery(query);
  }, [setSearchTerm, setSearchParams, setSortBy]);

  // Execute search after saved search state has settled
  useEffect(() => {
    if (!pendingSavedSearchRef.current) return;
    pendingSavedSearchRef.current = false;
    performSearch();
  }, [advancedFilters, performSearch]);

  // Trigger user search only when switching TO the users tab.
  // activeSearchQuery changes are handled by performSearch() directly (#1018).
  useEffect(() => {
    if (searchType === 'users' && hasSearched) {
      searchUsers(activeSearchQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchType, hasSearched, searchUsers]);

  // Filter artworks based on search and advanced filters
  const filteredArtworks = useMemo(() => {
    // If we have search results from the backend, use those
    if (hasSearched && searchResults.length > 0) {
      return searchResults.map((result) => ({
        artworkId: result.catalogItemId,
        userId: result.userId || '',
        status: 'READY' as const,
        createdAt: result.updatedAt,
        updatedAt: result.updatedAt,
        original: {
          filename: result.coverImageUrl || 'placeholder.jpg',
          size: 0,
          mimeType: 'image/jpeg',
          s3Key: result.catalogItemId
        },
        renditions: [],
        // Additional fields for display
        title: result.title,
        creator: result.creators?.join(', ') || '',
        workType: result.workType,
        techniques: result.techniques || [],
        materials: result.materials || [],
        creationYear: result.creationYear,
        coverImageUrl: result.coverImageUrl,
        aliCatalogNumber: result.aliCatalogNumber,
        visibility: result.visibility,
      }));
    }

    // No results until a search has been initiated
    if (!hasSearched) {
      return [];
    }

    // If search was performed but no results, return empty array
    return [];
  }, [searchResults, hasSearched]);

  const handleSearchKeyPress = async (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      await performSearch();
    }
  };

  const getGridColumns = () => {
    if (isMobile) return 'repeat(2, 1fr)';
    if (isTablet) return 'repeat(3, 1fr)';
    if (isLaptop) return 'repeat(4, 1fr)';
    return 'repeat(6, 1fr)';
  };

  const gridColumns = getGridColumns();

  return (
    <Box sx={{
      background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
      minHeight: '100vh'
    }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <DisplayNameNotice />
        {/* Centered Search Section */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: { xs: '30vh', sm: '40vh' },
          mb: { xs: 2, sm: 4 }
        }}>
          <Typography
            variant="h1"
            component="h1"
            sx={{
              color: theme.palette.primary.main,
              fontFamily: 'Libre Baskerville, serif',
              fontWeight: 'bold',
              mb: { xs: 1, sm: 2 },
              textAlign: 'center',
              textShadow: '0 2px 4px rgba(0,0,0,0.1)',
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
            }}
          >
            {en.app.title}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              textAlign: 'center',
              mb: { xs: 2, sm: 4 },
              maxWidth: { xs: 300, sm: 600 },
              lineHeight: 1.6,
              px: { xs: 2, sm: 0 }
            }}
          >
            Search and discover artwork and users by title, catalog ID, ALI ID, username, or full name.
          </Typography>

          <Box sx={{
            display: 'flex',
            gap: { xs: 1, sm: 2 },
            alignItems: 'center',
            width: '100%',
            maxWidth: { xs: '100%', sm: 600 },
            px: { xs: 2, sm: 0 }
          }}>
            <TextField
              placeholder="Search by title, catalog ID, ALI ID, username, or name..."
              size="medium"
              fullWidth
              value={localSearchInput}
              onChange={(e) => handleSearchQueryChange(e.target.value)}
              onKeyDown={handleSearchKeyPress}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {isSearching ? (
                      <CircularProgress size={20} sx={{ color: theme.palette.primary.main }} />
                    ) : (
                      <Search sx={{ color: theme.palette.primary.main }} />
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '1rem',
                  height: '48px',
                  borderRadius: '24px',
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  border: `1px solid ${theme.palette.divider}`,
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                    borderColor: theme.palette.primary.light,
                  },
                  '&.Mui-focused': {
                    boxShadow: `0 6px 16px ${theme.palette.primary.main}30`,
                    borderColor: theme.palette.primary.main,
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent',
                },
              }}
            />
            <AdvancedFilter
              onFilter={(filters) => {
                setAdvancedFilters(filters);
              }}
              loading={isSearching}
              initialFilters={advancedFilters}
            />
            {isAuthenticated && (
              <SavedSearchesMenu
                savedSearches={savedSearches}
                loading={savedSearchesLoading}
                error={savedSearchesError}
                onLoad={handleLoadSavedSearch}
                onDelete={deleteSearch}
              />
            )}
          </Box>
        </Box>

        {/* Search Error Display */}
        {searchError && (
          <Box sx={{ mb: 2, px: { xs: 2, sm: 0 } }}>
            <Alert severity="error" onClose={() => setHasSearched(false)}>
              Search failed: {searchError}
            </Alert>
          </Box>
        )}

        {/* Saved Searches Error */}
        {savedSearchesError && (
          <Box sx={{ mb: 2, px: { xs: 2, sm: 0 } }}>
            <Alert severity="warning">
              {savedSearchesError}
            </Alert>
          </Box>
        )}

        {/* Results Section - Always show */}
        <>
          {/* Results Tabs */}
          <Box sx={{ mb: { xs: 2, sm: 3 }, width: '100%', px: { xs: 2, sm: 0 } }}>
            <Tabs
              value={searchType}
              onChange={(_, newValue) => setSearchType(newValue)}
              variant={isMobile ? "fullWidth" : "standard"}
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: theme.palette.primary.main,
                }
              }}
            >
              <Tab
                value="objects"
                label={`Objects (${hasSearched ? total : filteredArtworks.length})`}
                icon={<Palette />}
                iconPosition={isMobile ? "top" : "start"}
                sx={{
                  textTransform: 'none',
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  fontWeight: 500,
                  color: searchType === 'objects' ? theme.palette.primary.main : 'text.secondary',
                  '&.Mui-selected': {
                    color: theme.palette.primary.main,
                  }
                }}
              />
              <Tab
                value="users"
                label={`Users (${userSearchResults.length})`}
                icon={<Person />}
                iconPosition={isMobile ? "top" : "start"}
                sx={{
                  textTransform: 'none',
                  fontSize: { xs: '0.8rem', sm: '1rem' },
                  fontWeight: 500,
                  color: searchType === 'users' ? theme.palette.primary.main : 'text.secondary',
                  '&.Mui-selected': {
                    color: theme.palette.primary.main,
                  }
                }}
              />
            </Tabs>
          </Box>

          {/* Objects Tab Content */}
          {searchType === 'objects' && (
            <>
              {hasSearched && (
                <SearchResultsToolbar
                  total={total}
                  sortBy={sortBy}
                  onSortChange={(newSort) => {
                    setSortBy(newSort);
                    setPage(0);
                  }}
                  onSaveSearch={() => setSaveDialogOpen(true)}
                  isAuthenticated={isAuthenticated}
                />
              )}
              <SearchResultsGrid
                artworks={filteredArtworks}
                gridColumns={gridColumns}
                isBookmarked={isBookmarked}
                toggleBookmark={toggleBookmark}
                currentUserId={permissions?.userId ?? null}
                canEdit={isAuthenticated ? canEditCatalog : undefined}
              />
              {(() => {
                const totalPages = Math.ceil(Math.min(total, 10000) / pageSize);
                return hasSearched && totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
                  <CatalogPagination
                    count={totalPages}
                    page={page + 1}
                    onChange={(_e, newPage) => {
                      setPage(newPage - 1);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                  />
                </Box>
              );
              })()}
            </>
          )}

          {/* Users Tab Content */}
          {searchType === 'users' && (
            <Box sx={{
              bgcolor: 'background.paper',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: { xs: 1, sm: 2 },
              mx: { xs: 1, sm: 0 }
            }}>
              {isSearchingUsers ? (
                <Box sx={{ textAlign: 'center', p: 4 }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Searching users...
                  </Typography>
                </Box>
              ) : userSearchError ? (
                <Alert severity="error" sx={{ m: 2 }}>
                  {userSearchError}
                </Alert>
              ) : userSearchResults.length === 0 ? (
                <Box sx={{ textAlign: 'center', p: 4 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                    {hasSearched ? 'No users found' : 'Search for users'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {hasSearched ? 'Try adjusting your search terms' : 'Enter a search term to find users'}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 2 } }}>
                  {userSearchResults.map((searchedUser) => (
                    <Box
                      key={searchedUser.userId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: { xs: 1.5, sm: 2 },
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        cursor: searchedUser.aliId ? 'pointer' : 'default',
                        '&:hover': searchedUser.aliId ? {
                          bgcolor: theme.palette.action.hover
                        } : undefined
                      }}
                      onClick={() => {
                        if (searchedUser.aliId) {
                          navigate(`/public/catalog/${searchedUser.aliId}`);
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 40, sm: 48 },
                          height: { xs: 40, sm: 48 },
                          borderRadius: '50%',
                          bgcolor: theme.palette.primary.main,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: { xs: 1.5, sm: 2 },
                          overflow: 'hidden'
                        }}
                      >
                        {searchedUser.profileImageUrl ? (
                          <img
                            src={searchedUser.profileImageUrl}
                            alt={searchedUser.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <Person sx={{ color: 'white', fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
                        )}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                          {searchedUser.name}
                        </Typography>
                        {searchedUser.aliId && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                            ALI ID: {searchedUser.aliId}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                          {searchedUser.artworkCount} artworks
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </>
      </Container>

      <SaveSearchDialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        onSave={handleSaveSearch}
        suggestedName={localSearchInput.trim()}
      />
    </Box>
  );
};
